/**
 * CONTRACT FILE. Destination: src/core/ops/types.ts
 * Implemented by package W1-03 (core-model-ops):
 *   - src/core/ops/ops.ts       exports `Ops: OpsApi`
 *   - src/core/doc/container.ts exports `createDocContainer: CreateDocContainer`
 *   - src/core/doc/txDraft.ts   exports `createTxDraft: CreateTxDraft` (THE TxBuilder implementation; pure core,
 *                               so command bodies, tools and their tests run anywhere, incl. workers and Node)
 *   - src/core/ops/shadow.ts    re-exports `SHADOW_RULES` (defined below) and applies it in `Ops.withShadow`
 *   - test/txHarness.ts         `runCmd(doc, cmd, args) -> { doc, ops, inverse }` for every package's command tests
 *
 * Typed, invertible, structured-clone-safe operations on the normalised document (ARCHITECTURE.md §5): patch
 * values `undefined` mean 'remove' / 'was absent', and numbers keep -0, so a plain JSON round trip is lossy. Encode
 * ops with `opsToJson` / `opsFromJson` (core/ops/opsJson.ts) wherever they must become JSON (e.g. a recovery-data
 * download).
 * The same op stream is: the undo log, the persistence journal, and the replication feed for workers.
 * `apply` is deliberately dumb (no business rules); business rules live in core/commands, which emit ops
 * through a TxBuilder. Invariants are validated per transaction for the records the ops touch.
 */

import type { Beat, RoundingReport, TempoMap } from '../time/types';
import type { MessageRef } from '../util/types';
import type {
  BpmId,
  ChartDoc,
  DocIndexes,
  EffectId,
  GroupId,
  InvariantViolation,
  KeyId,
  LineId,
  LineProp,
  MarkerId,
  NoteId,
  OrderListName,
  SingletonName,
  SingletonRecords,
  TableIds,
  TableName,
  TableRecords,
  TrackId,
  VideoId,
} from '../model/types';

/**
 * Shallow patch of a record's top-level fields. `undefined` removes an OPTIONAL field (e.g. `src`).
 * Nested objects are replaced as a whole (records are immutable values).
 */
export type RecordPatch<R> = { readonly [K in keyof R]?: R[K] | undefined };

export type InsOp = { [T in TableName]: { readonly o: 'ins'; readonly tbl: T; readonly rec: TableRecords[T] } }[TableName];
/** `rec` is the full record being deleted, so the inverse is an insert (cheap undo of mass deletes). */
export type DelOp = { [T in TableName]: { readonly o: 'del'; readonly tbl: T; readonly rec: TableRecords[T] } }[TableName];
export type SetOp = {
  [T in TableName]: {
    readonly o: 'set';
    readonly tbl: T;
    readonly id: TableIds[T];
    /** Previous values of exactly the fields in `next` (undefined = field was absent). */
    readonly prev: RecordPatch<TableRecords[T]>;
    readonly next: RecordPatch<TableRecords[T]>;
  };
}[TableName];
export type SingleOp = {
  [S in SingletonName]: {
    readonly o: 'single';
    readonly key: S;
    readonly prev: RecordPatch<SingletonRecords[S]>;
    readonly next: RecordPatch<SingletonRecords[S]>;
  };
}[SingletonName];
export interface OrderOp {
  readonly o: 'order';
  readonly list: OrderListName;
  readonly prev: readonly number[];
  readonly next: readonly number[];
}
/** Id counter advance. Never undone (ids are never reused); its inverse is itself. */
export interface IdSeqOp {
  readonly o: 'idSeq';
  readonly next: number;
}

export type Op = InsOp | DelOp | SetOp | SingleOp | OrderOp | IdSeqOp;

/** Ids of everything selected, captured with every transaction (restored on undo/redo). */
export interface SelectionSnapshot {
  readonly notes: readonly NoteId[];
  /** Keys selected as points (diamonds). */
  readonly keys: readonly KeyId[];
  /** Segments selected, identified by the key they START at. */
  readonly segments: readonly KeyId[];
  readonly lines: readonly LineId[];
  readonly bpm: readonly BpmId[];
  readonly markers: readonly MarkerId[];
  readonly effects: readonly EffectId[];
  readonly videos: readonly VideoId[];
  readonly groups: readonly GroupId[];
  /** Active line (whose notes and motion are edited). */
  readonly activeLine: LineId | null;
}

/** "Jump to change" target used by undo/redo (NAV-17). */
export interface TxnFocus {
  readonly beat?: Beat;
  readonly line?: LineId;
}

export type TxnKind = 'do' | 'undo' | 'redo' | 'load' | 'import' | 'restore';

/**
 * How a committed Txn affects the undo history. Persisted undo is DERIVED from the journal by folding these
 * markers over the retained window (ARCHITECTURE.md §10): there is no separate history store.
 *  - push:  new history entry (truncates the redo tail);
 *  - merge: coalesced into the entry committed at seq `into`;
 *  - undo / redo: inverse / re-application of the entry committed at seq `of`;
 *  - reset: history cleared (Clear history, or the store found the history stale; the transaction has no ops).
 *    Version restore is a `push` (one undoable step); `load` writes no marker.
 */
export type HistMarker =
  | { readonly k: 'push' }
  | { readonly k: 'merge'; readonly into: number }
  | { readonly k: 'undo'; readonly of: number }
  | { readonly k: 'redo'; readonly of: number }
  | { readonly k: 'reset' };

/** A committed transaction (one user action). */
export interface Txn {
  /** Monotonic per project; the journal key. */
  readonly seq: number;
  readonly kind: TxnKind;
  /** Human-readable label, e.g. {key: 'txn.moveNotes', params: {count: 12, dt: '+1/4'}}. */
  readonly label: MessageRef;
  readonly ops: readonly Op[];
  /** Epoch ms. */
  readonly at: number;
  /** Same key + within 800 ms + nothing in between => merged into the previous entry. */
  readonly coalesceKey?: string;
  readonly selBefore: SelectionSnapshot;
  readonly selAfter: SelectionSnapshot;
  readonly focus?: TxnFocus;
  /** Command that produced it (diagnostics, history panel icon). */
  readonly command?: string;
  /** Set by DocStore at commit; see HistMarker. */
  readonly hist: HistMarker;
}

/** What changed, for incremental consumers (engine, timeline redraw, checker, inspector selectors). */
export interface DirtyScopes {
  readonly tempo: boolean;
  /** offsetMs / infoOffsetSec / meter. */
  readonly timing: boolean;
  readonly meta: boolean;
  /** Package settings (speed mode, aspect, lineLength, flags) */
  readonly pkg: boolean;
  readonly editor: boolean;
  readonly rpeRoot: boolean;
  /** lineOrder changed or lines added/removed. */
  readonly lineSet: boolean;
  /** Line record fields changed (parent, zOrder, texture, attachUI, cover, controls, names...). */
  readonly lineProps: ReadonlySet<LineId>;
  /** Tracks/keys changed, per line and property. */
  readonly lineMotion: ReadonlyMap<LineId, ReadonlySet<LineProp>>;
  /** Notes added/removed/changed on these lines (a moved note dirties both lines). */
  readonly lineNotes: ReadonlySet<LineId>;
  /** Effects, effectOrder, videos or their var tracks. */
  readonly effects: boolean;
  readonly markers: boolean;
  readonly groups: boolean;
  readonly assets: boolean;
  readonly generators: boolean;
}

export type ChangeSource = 'txn' | 'gesture' | 'gestureCancel' | 'undo' | 'redo' | 'load' | 'replicaResync';

export interface ChangeSet {
  readonly source: ChangeSource;
  /** Present for committed sources ('txn', 'undo', 'redo'); absent for live gesture updates. */
  readonly seq?: number;
  readonly ops: readonly Op[];
  readonly dirty: DirtyScopes;
}

/**
 * Mutable document container: the record Maps, derived indexes and `apply`. Used by the main-thread
 * DocStore and by every worker replica (same code, so replicas stay identical).
 */
export interface DocContainer {
  /** Live view; the Maps belong to the container and change on apply. Records are immutable. */
  readonly doc: ChartDoc;
  readonly idx: DocIndexes;
  apply(op: Op): void;
  applyAll(ops: readonly Op[]): void;
}

export type CreateDocContainer = (doc: ChartDoc) => DocContainer;

export interface OpsApi {
  invert(op: Op): Op;
  /** Inverse of a sequence: inverted ops in reverse order. */
  invertAll(ops: readonly Op[]): Op[];
  /**
   * Coalesce a sequence: consecutive sets on the same record collapse (first prev, last next),
   * ins followed by sets fold into the ins, ins followed by del cancels. Result applies identically.
   */
  compose(ops: readonly Op[]): Op[];
  /** Dirty scopes of a sequence, resolved against the document state BEFORE the ops (for del/ins line lookups). */
  dirtyOf(ops: readonly Op[], before: ChartDoc): DirtyScopes;
  emptyDirty(): DirtyScopes;
  mergeDirty(a: DirtyScopes, b: DirtyScopes): DirtyScopes;
  /** Validate invariants of the records touched by `ops` (fast path used on every commit). */
  validateTouched(doc: ChartDoc, idx: DocIndexes, ops: readonly Op[]): readonly InvariantViolation[];
  /** Fields of `patch` that shadow passthrough provenance, expanded into the extra clears (see SHADOW_RULES). */
  withShadow<T extends TableName>(tbl: T, current: TableRecords[T], patch: RecordPatch<TableRecords[T]>): RecordPatch<TableRecords[T]>;
  /**
   * Ops that turn document `from` into document `to` (ins/del/set/single/order per record, plus one idSeq op
   * with max(from.idSeq, to.idSeq)). Used for version restore as ONE undoable transaction, sidecar re-anchoring
   * (P1) and applying a whole re-lifted track: `tx.applyOps(Ops.diff(tx.doc, target))`.
   */
  diff(from: ChartDoc, to: ChartDoc): Op[];
}

/**
 * THE TRANSACTION BUILDER: the only way commands mutate the document.
 * Implemented by core/doc/txDraft.ts (`createTxDraft`); the main-thread DocStore wraps it and adds only
 * validation, apply, history and broadcast. Reads reflect pending writes (draft overlay over the container;
 * the container itself is untouched until the DocStore applies `finish().ops`).
 * All writes are validated at commit; an invalid transaction is rejected whole.
 */
export interface TxBuilder {
  readonly doc: ChartDoc;
  readonly idx: DocIndexes;
  /** Tempo map of the CURRENT draft (rebuilt lazily if the transaction changed the BPM table). */
  readonly tempo: TempoMap;
  /** Rounding counter for rational arithmetic done by the command (reported in the label/toast). */
  readonly rounding: RoundingReport;
  /** Fresh numeric id for a record of table `tbl` (advances idSeq). Assets are content-addressed instead. */
  newId<K extends Exclude<TableName, 'assets'>>(tbl: K): TableIds[K];
  insert<T extends TableName>(tbl: T, rec: TableRecords[T]): void;
  /** Applies SHADOW_RULES automatically (editing a modelled field drops the provenance it shadows). */
  update<T extends TableName>(tbl: T, id: TableIds[T], patch: RecordPatch<TableRecords[T]>): void;
  remove<T extends TableName>(tbl: T, id: TableIds[T]): void;
  updateSingleton<S extends SingletonName>(key: S, patch: RecordPatch<SingletonRecords[S]>): void;
  setOrder(list: OrderListName, next: readonly number[]): void;
  /**
   * Record raw ops (e.g. from `Ops.diff`). Each op's `prev`/`rec` must match the draft state (else `fail` with
   * 'ops.stale'); idSeq ops only ever raise the counter. Shadow rules are NOT applied (the ops are explicit).
   */
  applyOps(ops: readonly Op[]): void;
  /** Abort the transaction with a user-facing reason. Nothing is applied. */
  fail(reason: MessageRef): never;
  /** Non-fatal note shown in the toast (e.g. "3 segments skipped: speed allows only linear"). */
  note(msg: MessageRef): void;
  setFocus(focus: TxnFocus): void;
  /** Selection after the transaction (defaults to the selection before). */
  setSelection(sel: Partial<SelectionSnapshot>): void;
}

/** What a finished draft produced (the DocStore validates, applies and commits it). */
export interface TxDraftResult {
  /** Ops in application order (shadow rules applied, idSeq advanced once at the end). */
  readonly ops: readonly Op[];
  readonly notes: readonly MessageRef[];
  readonly focus: TxnFocus | null;
  readonly selection: Partial<SelectionSnapshot> | null;
  readonly rounding: RoundingReport;
}

/**
 * One transaction in progress over a container. Pure core: draft overlay (reads see pending writes),
 * Ops.withShadow on every update, newId/idSeq, lazy createTempoMap when the BPM table changed, `fail` aborts.
 */
export interface TxDraft {
  readonly tx: TxBuilder;
  /** Runs `build` against the draft; a `tx.fail(...)` inside aborts the draft (nothing recorded) and returns the reason. */
  run<R>(build: (tx: TxBuilder) => R): { readonly ok: true; readonly value: R } | { readonly ok: false; readonly reason: MessageRef };
  /** Ends the draft and returns everything recorded. The draft cannot be used afterwards. */
  finish(): TxDraftResult;
  /** Discards everything recorded. */
  abort(): void;
}

export type CreateTxDraft = (c: DocContainer, sel: SelectionSnapshot) => TxDraft;

/**
 * Shadow rules (PRO §4.7, judge-tech graft 5): editing a modelled field removes the provenance fields
 * that encode the same meaning, so raw values can never contradict the model. Paths are relative to the
 * record; one level of nesting ('src.rawStart', 'rpe.isCoverRaw').
 */
export interface ShadowRule {
  readonly tbl: TableName | 'pkg';
  readonly field: string;
  readonly clears: readonly string[];
}

export const SHADOW_RULES: readonly ShadowRule[] = [
  { tbl: 'keys', field: 't', clears: ['src.rawStart', 'src.rawEnd'] },
  { tbl: 'keys', field: 'ease', clears: ['src.easingType', 'src.ignoredEase', 'src.sliceRaw', 'src.bezierPointsRaw', 'src.rawEndValue'] },
  { tbl: 'keys', field: 'vOut', clears: ['src.rawEndValue'] },
  { tbl: 'notes', field: 't', clears: ['src.rawStart', 'src.nonHoldEnd'] },
  { tbl: 'notes', field: 'end', clears: ['src.rawEnd', 'src.nonHoldEnd'] },
  { tbl: 'notes', field: 'kind', clears: ['src.nonHoldEnd', 'src.rawEnd'] },
  { tbl: 'notes', field: 'above', clears: ['src.aboveRaw'] },
  { tbl: 'notes', field: 'fake', clears: ['src.isFakeRaw'] },
  { tbl: 'bpm', field: 't', clears: ['src.rawStart'] },
  { tbl: 'effects', field: 'start', clears: ['src.rawStart'] },
  { tbl: 'effects', field: 'end', clears: ['src.rawEnd'] },
  { tbl: 'lines', field: 'cover', clears: ['rpe.isCoverRaw'] },
  { tbl: 'pkg', field: 'rpeVersion', clears: ['rpeVersionRaw'] },
];

/** Re-export for convenience of command code. */
export type { TrackId };
